import { startCase } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VButton, VFlex, VInput, VStack } from '@/package';
import type { Task, User } from '@/type';

import {
  type MemberTaskBaseRow,
  MemberTaskBaseTable,
} from './MemberTaskBaseTable';
import {
  useDefaultReallocateRatioValue,
  useReallocateRatioErrorMessage,
  useWorkReallocateRatioForm,
  useWorkReallocateRatioSubmit,
} from './hook';
import type { WorkReallocateRatioFormData } from './type';

type MemberReallocateRatioTableProps = {
  task: Task;
};

const formatNumber = (value: number) => Intl.NumberFormat().format(value);

export const MemberReallocateRatioTable = ({
  task,
}: MemberReallocateRatioTableProps) => {
  const { t } = useTranslation();
  const { formState, register, setValue, watch } =
    useFormContext<WorkReallocateRatioFormData>();
  const [rowList, setRowList] = useState<
    MemberTaskBaseRow<{
      completedAndTotal: string;
      index: number;
      inProgress: string;
      remaining: number;
      reassigned: number;
      taskRatio: number;
    }>[]
  >([]);
  const userTask = task.userTask ?? [];
  const indexByUserID = useMemo(() => {
    return userTask.reduce(
      (acc, userTask, index) => {
        acc[userTask.user.id] = index;

        return acc;
      },
      {} as { [userID: User['id']]: number },
    );
  }, [userTask]);
  const distributions = watch('distributions');
  const taskRatioByUserIDRef = useRef<{ [userID: User['id']]: number }>({});

  const setTotalTaskRatio = (totalTaskRatio: number) => {
    setValue('totalTaskRatio', Number(totalTaskRatio.toFixed(2)), {
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const calculateTotalTaskRatio = () => {
    let total = 0;

    Object.values(taskRatioByUserIDRef.current).forEach(taskRatio => {
      total += taskRatio;
    });

    setTotalTaskRatio(total);
  };

  const redistribute = () => {
    const fullFrameCount = task.fullFrameCount;

    const userDataList = distributions.map(
      ({ userID, allocateRatio }, index) => {
        const userTask = task.userTask?.find(user => user.user.id === userID);
        const user = userTask?.user;
        const taskRatio = taskRatioByUserIDRef.current[userID] ?? allocateRatio;
        const exactValue = fullFrameCount * (taskRatio / 100);
        const floorValue = Math.floor(exactValue);
        const remainder = exactValue - floorValue;

        if (!taskRatioByUserIDRef.current[userID]) {
          taskRatioByUserIDRef.current[userID] = taskRatio;
        }

        return {
          index,
          userID,
          user,
          userTask,
          taskRatio,
          floorValue,
          remainder,
          reassigned: floorValue,
        };
      },
    );

    const floorSum = userDataList.reduce((acc, cur) => acc + cur.floorValue, 0);
    let remainingFrame = fullFrameCount - floorSum;

    const sortedByRemainder = [...userDataList].sort(
      (a, b) => b.remainder - a.remainder,
    );

    sortedByRemainder.forEach(userData => {
      if (remainingFrame > 0) {
        userData.reassigned = userData.floorValue + 1;
        remainingFrame--;
      }
    });

    // 4단계: 원래 순서로 rowList 생성
    const rowList: MemberTaskBaseRow<{
      completedAndTotal: string;
      index: number;
      inProgress: string;
      remaining: number;
      reassigned: number;
      taskRatio: number;
    }>[] = [];

    userDataList.forEach(
      ({ user, userID, userTask, taskRatio, reassigned, index }) => {
        if (!user || !userTask) return;

        rowList.push({
          ...user,
          id: userID,
          index,
          taskRatio,
          name: user.name || user.email,
          inProgress: formatNumber(userTask.inProgressFrameCount),
          completedAndTotal: `${formatNumber(userTask.completedFrameCount)} / ${formatNumber(userTask.totalFrameCount)}`,
          remaining:
            userTask.totalFrameCount -
            userTask.inProgressFrameCount -
            userTask.completedFrameCount,
          reassigned,
        } as MemberTaskBaseRow<{
          completedAndTotal: string;
          index: number;
          inProgress: string;
          remaining: number;
          reassigned: number;
          taskRatio: number;
        }>);
      },
    );

    setRowList(rowList);
    calculateTotalTaskRatio();
  };

  const handleTaskRatioChange = (id: User['id'], taskRatio: number) => {
    setValue(
      `distributions.${indexByUserID[id]}`,
      {
        allocateRatio: taskRatio,
        userID: id,
      },
      {
        shouldTouch: true,
        shouldValidate: true,
      },
    );

    taskRatioByUserIDRef.current[id] = taskRatio;
  };

  useEffect(() => {
    taskRatioByUserIDRef.current = {};

    redistribute();
  }, [JSON.stringify(distributions), task.userTask]);

  return (
    <VStack className="flex-1 overflow-hidden" gap={0.5}>
      <ul className="list-inside! list-disc! pt-2 text-sm">
        <li>{t('task.distributeDescription0.browning')}</li>
        <li>{t('task.distributeDescription1.volumette')}</li>
        <li>{t('task.distributeDescription2.scalene')}</li>
      </ul>

      <div className="flex-1 overflow-hidden">
        <MemberTaskBaseTable
          data={rowList}
          middleColumn={[
            {
              column: 'inProgress',
              renderColumn: column => startCase(column),
              renderCell: value => (
                <VInput
                  inputClassName="text-right"
                  defaultValue={value}
                  readOnly
                  size="sm"
                  tabIndex={-1}
                />
              ),
            },
            {
              column: 'completedAndTotal',
              renderColumn: column => startCase(column).replace(/and/gi, '/'),
              renderCell: value => (
                <VInput
                  inputClassName="text-right"
                  defaultValue={value}
                  readOnly
                  size="sm"
                  tabIndex={-1}
                />
              ),
            },
            {
              column: 'remaining',
              renderColumn: column => startCase(column),
              renderCell: value => (
                <VInput
                  inputClassName="text-right"
                  defaultValue={formatNumber(value as number)}
                  readOnly
                  size="sm"
                  tabIndex={-1}
                />
              ),
            },
            {
              column: 'reassigned',
              renderColumn: column => startCase(column),
              renderCell: value => {
                return (
                  <VInput
                    key={value}
                    defaultValue={formatNumber(value as number)}
                    readOnly
                    size="sm"
                    tabIndex={-1}
                    type="number"
                  />
                );
              },
            },
          ]}
          removable={false}
          onTaskRatioChange={handleTaskRatioChange}
        />
      </div>
      <VFlex
        className="border-t border-gray-500 px-2 py-1 text-body-md text-gray-500"
        items="center"
      >
        <VFlex className="flex-1 [&>div]:px-2 [&>div]:py-1" items="center">
          <div className="flex-1">Total</div>

          <div className="w-[100px]">
            <VInput
              readOnly
              size="sm"
              value={formatNumber(
                rowList.reduce((acc, cur) => acc + cur.remaining, 0),
              )}
            />
          </div>
          <div className="w-[100px]">
            <VInput
              readOnly
              size="sm"
              value={formatNumber(
                rowList.reduce((acc, cur) => acc + cur.reassigned, 0),
              )}
            />
          </div>
          <div className="w-[88px]">
            <VInput
              {...register('totalTaskRatio', {
                max: {
                  value: 100,
                  message: 'The total task ratio must be 100%',
                },
                min: {
                  value: 100,
                  message: 'The total task ratio must be 100%',
                },
              })}
              fluid
              readOnly
              renderRightSide={() => '%'}
              size="sm"
              type="number"
              valid={!!formState.errors.totalTaskRatio}
            />
          </div>
        </VFlex>
      </VFlex>
    </VStack>
  );
};

type MemberReallocateRatioTableFormProps = {
  distributeEvenlyTimestamp?: number;
  task: Task;
  onCancel?(): void;
};

export const MemberReallocateRatioTableForm = ({
  distributeEvenlyTimestamp,
  task,
  onCancel,
}: MemberReallocateRatioTableFormProps) => {
  const { t } = useTranslation();
  const { formState, setValue } = useFormContext<WorkReallocateRatioFormData>();
  const { onSubmit } = useWorkReallocateRatioSubmit(task, () => onCancel?.());
  const { errorMessage } = useReallocateRatioErrorMessage();
  const { isValid } = formState;
  const { makeDefaultValue } = useDefaultReallocateRatioValue(task);

  const init = () => {
    const { distributions, totalTaskRatio } = makeDefaultValue();

    setValue('distributions', distributions, {
      shouldTouch: true,
      shouldValidate: true,
    });

    setValue('totalTaskRatio', totalTaskRatio, {
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (!distributeEvenlyTimestamp || distributeEvenlyTimestamp <= 0) return;

    init();
  }, [distributeEvenlyTimestamp]);

  useEffect(() => {
    init();
  }, []);

  return (
    <form className="flex h-full flex-col overflow-hidden" onSubmit={onSubmit}>
      <MemberReallocateRatioTable task={task} />

      <VStack>
        <VFlex items="center" justify="between" className="pt-2 pb-4">
          <div className="text-body-sm text-error-500">{errorMessage}</div>
          <VFlex items="center" gap={0.5}>
            <VButton
              outlined
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => onCancel?.()}
            >
              {t('task.distributeCancel.drumskin')}
            </VButton>
            <VButton
              type="submit"
              size="sm"
              disabled={!isValid || !!errorMessage}
            >
              {t('task.distributeComplete.squeeze')}
            </VButton>
          </VFlex>
        </VFlex>
      </VStack>
    </form>
  );
};

export const MemberReallocateRatioTableFormWrapper = ({
  distributeEvenlyTimestamp,
  task,
  onCancel,
}: MemberReallocateRatioTableFormProps) => {
  const distributionForm = useWorkReallocateRatioForm(task);

  return (
    <FormProvider {...distributionForm}>
      <MemberReallocateRatioTableForm
        distributeEvenlyTimestamp={distributeEvenlyTimestamp}
        task={task}
        onCancel={onCancel}
      />
    </FormProvider>
  );
};

MemberReallocateRatioTable.displayName = 'MemberReallocateRatioTable';
MemberReallocateRatioTableForm.displayName = 'MemberReallocateRatioTableForm';
MemberReallocateRatioTableFormWrapper.displayName =
  'MemberReallocateRatioTableFormWrapper';
